import {
  IWorkflowKeyWordRule,
  IWorkflowKeyWordsForm,
} from "@/types/workflow-keywords.types";

const MAX_JSON_LENGTH = 1024;

export const parseWorkflowKeyWords = (raw?: string | null): IWorkflowKeyWordRule[] => {
  if (!raw || raw.trim() === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as IWorkflowKeyWordsForm;
    if (!Array.isArray(parsed?.rules)) {
      return [];
    }

    return parsed.rules
      .filter((rule) => rule && typeof rule === "object")
      .map((rule) => ({
        keywords: Array.isArray(rule.keywords)
          ? rule.keywords.filter((k) => typeof k === "string" && k.trim() !== "")
          : [],
        destination: typeof rule.destination === "string" ? rule.destination.trim() : "",
      }))
      .filter((rule) => rule.keywords.length > 0 && rule.destination !== "");
  } catch {
    return [];
  }
};

export const finalizeWorkflowKeyWordRules = (
  rules: IWorkflowKeyWordRule[],
  drafts: Record<number, string> = {}
): IWorkflowKeyWordRule[] => {
  return rules.map((rule, index) => {
    const pendingKeyword = (drafts[index] || "").trim();
    if (!pendingKeyword || rule.keywords.includes(pendingKeyword)) {
      return rule;
    }

    return {
      ...rule,
      keywords: [...rule.keywords, pendingKeyword],
    };
  });
};

export const validateWorkflowKeyWordRules = (
  rules: IWorkflowKeyWordRule[],
  drafts: Record<number, string> = {}
): { isValid: boolean; incompleteRuleIndexes: number[] } => {
  const incompleteRuleIndexes: number[] = [];

  rules.forEach((rule, index) => {
    const pendingKeyword = (drafts[index] || "").trim();
    const hasKeyword =
      (rule.keywords || []).some((keyword) => keyword.trim() !== "") || pendingKeyword !== "";
    const hasDestination = (rule.destination || "").trim() !== "";

    if (!hasKeyword || !hasDestination) {
      incompleteRuleIndexes.push(index);
    }
  });

  return {
    isValid: incompleteRuleIndexes.length === 0,
    incompleteRuleIndexes,
  };
};

export const serializeWorkflowKeyWords = (rules: IWorkflowKeyWordRule[]): string | null => {
  const sanitizedRules = rules
    .map((rule) => ({
      keywords: (rule.keywords || [])
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword !== ""),
      destination: (rule.destination || "").trim(),
    }))
    .filter((rule) => rule.keywords.length > 0 && rule.destination !== "");

  if (sanitizedRules.length === 0) {
    return null;
  }

  const json = JSON.stringify({ rules: sanitizedRules });
  if (json.length > MAX_JSON_LENGTH) {
    throw new Error("WORKFLOW_KEYWORDS_TOO_LONG");
  }

  return json;
};
